import { useEffect, useState } from "react";
import { Cpu, MemoryStick, HardDrive } from "lucide-react";
import { API, BACKEND } from "@/lib/api";


const fmtUptime = (s) => {
    if (!s && s !== 0) return "—";
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d) return `${d}d ${h}h`;
    if (h) return `${h}h ${m}m`;
    return `${m}m`;
};

const Bar = ({ value, max = 100, tone = "cyan" }) => (
    <div className="h-1 flex-1 overflow-hidden rounded-full bg-slate-800">
        <div
            className={`h-full rounded-full transition-all duration-700 ${
                value > 85 ? "bg-rose-400" : tone === "teal" ? "bg-teal-400" : "bg-cyan-400"
            }`}
            style={{ width: `${Math.min(100, (value / max) * 100)}%` }}
        />
    </div>
);

export default function ChildrenGrid({ appId, metrics }) {
    const [children, setChildren] = useState([]);
    const [system, setSystem] = useState(null);
    const [lastAt, setLastAt] = useState(null);
    const [nowTs, setNowTs] = useState(Date.now());

    useEffect(() => {
        const t = setInterval(() => setNowTs(Date.now()), 5000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        let dead = false;
        (async () => {
            try {
                const r = await fetch(`${API}/apps/${appId}/children`, { credentials: "include" });
                if (r.ok) {
                    const d = await r.json();
                    const real = (d.children || []).filter((c) => !c.simulated);
                    if (!dead && real.length) {
                        setChildren(real);
                        const newest = real
                            .map((c) => c.updated_at)
                            .filter(Boolean)
                            .sort()
                            .pop();
                        if (newest) setLastAt(newest);
                    }
                }
            } catch {
                /* best-effort */
            }
        })();
        return () => {
            dead = true;
        };
    }, [appId]);

    useEffect(() => {
        if (!metrics || metrics.simulated) return;
        setChildren(metrics.children || []);
        const sys = metrics.system || null;
        // custom agents may report children only — don't render empty gauges
        const hasNumbers =
            sys && [sys.cpu, sys.mem_pct, sys.disk_pct].some((v) => typeof v === "number");
        setSystem(hasNumbers ? sys : null);
        setLastAt(metrics.at || new Date().toISOString());
    }, [metrics]);

    const age = lastAt ? (nowTs - new Date(lastAt).getTime()) / 1000 : null;
    const agentState = !lastAt ? "none" : age > 90 ? "offline" : "live";
    const badge = {
        none: ["No agent connected", "border-slate-700 bg-slate-900 text-slate-400"],
        live: ["Live agent data", "border-emerald-500/30 bg-emerald-950/40 text-emerald-300"],
        offline: ["Agent offline", "border-rose-500/30 bg-rose-950/40 text-rose-300"],
    }[agentState];

    return (
        <div data-testid="children-grid" className="glass-panel rounded-xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-display text-sm font-bold tracking-tight text-slate-100">
                    Child hosts
                </h3>
                <span
                    data-testid="children-source-badge"
                    className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest ${badge[1]}`}
                >
                    {badge[0]}
                </span>
            </div>

            {system && (
                <div className="mt-4 grid grid-cols-3 gap-4 rounded-lg border border-slate-800/80 bg-slate-950/50 px-4 py-3">
                    {[
                        [Cpu, "CPU", system.cpu, "%"],
                        [MemoryStick, "Memory", system.mem_pct, "%"],
                        [HardDrive, "Disk", system.disk_pct, "%"],
                    ].map(([Icon, label, v, unit]) => (
                        <div key={label} className="flex items-center gap-2.5">
                            <Icon className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                            <div className="flex flex-1 items-center gap-2">
                                <Bar value={v || 0} />
                                <span className="font-mono text-[10px] text-slate-400">
                                    {v}
                                    {unit}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className={`mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3 ${agentState === "offline" ? "opacity-60" : ""}`}>
                {children.length === 0 && (
                    <p className="col-span-full py-6 text-center font-mono text-[11px] text-slate-600">
                        Nothing discovered yet — run the agent above and your projects appear here.
                    </p>
                )}
                {children.map((c) => (
                    <div
                        key={`${c.host_id || "host"}-${c.name}-${c.pid}`}
                        data-testid={`child-host-${c.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                        className="rounded-lg border border-slate-800/80 bg-[#111827] p-4 transition-colors hover:border-cyan-500/30"
                    >
                        <div className="flex items-center justify-between gap-2">
                            <span className="truncate font-mono text-[11px] font-semibold text-slate-200">
                                {c.name}
                            </span>
                            <span
                                className={`shrink-0 rounded border px-1.5 py-px font-mono text-[9px] uppercase tracking-wider ${
                                    c.kind === "node-project"
                                        ? "border-teal-500/30 bg-teal-950/40 text-teal-300"
                                        : c.kind === "ssh-session"
                                          ? "border-cyan-500/30 bg-cyan-950/40 text-cyan-300"
                                          : "border-slate-700 bg-slate-900 text-slate-400"
                                }`}
                            >
                                {c.kind === "node-project" ? "node" : c.kind === "ssh-session" ? "ssh" : "process"}
                            </span>
                        </div>
                        <div className="mt-3 space-y-2">
                            <div className="flex items-center gap-2 font-mono text-[10px] text-slate-500">
                                <span className="w-8">cpu</span>
                                <Bar value={c.cpu || 0} />
                                <span className="w-12 text-right text-slate-400">{c.cpu}%</span>
                            </div>
                            <div className="flex items-center gap-2 font-mono text-[10px] text-slate-500">
                                <span className="w-8">mem</span>
                                <Bar value={c.mem_mb || 0} max={4096} tone="teal" />
                                <span className="w-12 text-right text-slate-400">{c.mem_mb}MB</span>
                            </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between font-mono text-[10px] text-slate-600">
                            <span className="flex items-center gap-1.5">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-dot" />
                                up {fmtUptime(c.uptime_s)}
                            </span>
                            {c.pid && <span>pid {c.pid}</span>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
